import {
  init as initCore,
  registerImageLoader,
  metaData
} from '@cornerstonejs/core';
import { init as initTools } from '@cornerstonejs/tools';
import dicomParser from 'dicom-parser';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';

/**
 * Initialize all required Cornerstone libraries
 */
export async function initCornerstone() {
  try {
    // Initialize Cornerstone Core
    await initCore();
    
    // Initialize Cornerstone Tools
    await initTools();
    
    // Set up cornerstone-core
    cornerstone.imageCache.setMaximumSizeBytes(3000000000);
    
    // Configure cornerstone-wado-image-loader
    cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
    cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
    
    // Disable web workers for simplicity in development
    cornerstoneWADOImageLoader.webWorkerManager.terminate();
    cornerstoneWADOImageLoader.webWorkerManager.initialize({
      maxWebWorkers: 0
    });
    
    // Configure for loading DICOM files
    cornerstoneWADOImageLoader.configure({
      useWebWorkers: false,
      decodeConfig: {
        // JPEG Baseline (Process 1)
        '1.2.840.10008.1.2.4.50': {},
        // MPEG-4 AVC/H.264
        '1.2.840.10008.1.2.4.102': {},
        // MPEG-4 HEVC/H.265
        '1.2.840.10008.1.2.4.103': {}
      }
    });
    
    // Set up metadata providers
    function metaDataProvider(type, imageId) {
      const parsedImageId = imageId.replace('cs3d:', '');
      return cornerstoneWADOImageLoader.wadors.metaDataProvider(type, parsedImageId);
    }
    
    metaData.addProvider(metaDataProvider);
    
    // Register a custom image loader for cs3d scheme
    registerImageLoader('cs3d', (imageId) => {
      const parsedImageId = imageId.replace('cs3d:', '');
      
      // Load image using cornerstone-core and return a promise
      return new Promise((resolve, reject) => {
        cornerstone.loadImage(parsedImageId).then(
          (image) => {
            // Convert cornerstone-core image to a format Cornerstone3D can use
            const newImage = {
              ...image,
              getPixelData: image.getPixelData,
              sizeInBytes: image.sizeInBytes
            };
            resolve(newImage);
          },
          (error) => {
            console.error('Error loading image:', error);
            reject(error);
          }
        );
      });
    });
    
    console.log('Cornerstone libraries initialized successfully');
    return true;
  } catch (error) {
    console.error('Error initializing Cornerstone:', error);
    throw error;
  }
}

/**
 * Create a proper Cornerstone imageId for a File object
 * @param {File} file - The DICOM file
 * @returns {string} - The imageId to use with Cornerstone
 */
export function createImageId(file) {
  // Create a blob URL for the file
  const objectUrl = URL.createObjectURL(file);
  
  // Create wadouri URL with the object URL, wrapped in cs3d scheme
  return `cs3d:wadouri:${objectUrl}`;
}

/**
 * Read a DICOM file and parse it to extract metadata
 * @param {File} file - The DICOM file to read
 * @returns {Promise<Object>} - The parsed DICOM dataset
 */
export function readDICOMFile(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      try {
        const dicomDataset = dicomParser.parseDicom(new Uint8Array(e.target.result));
        resolve(dicomDataset);
      } catch (error) {
        reject(error);
      }
    };
    fileReader.onerror = () => {
      reject(new Error('Failed to read DICOM file'));
    };
    fileReader.readAsArrayBuffer(file);
  });
}

/**
 * Check if a DICOM file uses MP4 transfer syntax
 * @param {Object} dicomDataset - Parsed DICOM dataset
 * @returns {boolean} - True if MP4 transfer syntax is used
 */
export function isMP4TransferSyntax(dicomDataset) {
  const transferSyntaxUID = dicomDataset.string('x00020010');
  return transferSyntaxUID === '1.2.840.10008.1.2.4.102' || 
         transferSyntaxUID === '1.2.840.10008.1.2.4.103';
}

/**
 * Get the transfer syntax description
 * @param {Object} dicomDataset - Parsed DICOM dataset
 * @returns {string} - Description of the transfer syntax
 */
export function getTransferSyntaxDescription(dicomDataset) {
  const transferSyntaxUID = dicomDataset.string('x00020010');
  
  const syntaxDescriptions = {
    '1.2.840.10008.1.2': 'Implicit VR Little Endian',
    '1.2.840.10008.1.2.1': 'Explicit VR Little Endian',
    '1.2.840.10008.1.2.2': 'Explicit VR Big Endian',
    '1.2.840.10008.1.2.4.50': 'JPEG Baseline (Process 1)',
    '1.2.840.10008.1.2.4.51': 'JPEG Extended (Process 2 & 4)',
    '1.2.840.10008.1.2.4.57': 'JPEG Lossless, Non-Hierarchical (Process 14)',
    '1.2.840.10008.1.2.4.70': 'JPEG Lossless, Non-Hierarchical, First-Order Prediction',
    '1.2.840.10008.1.2.4.80': 'JPEG-LS Lossless Image Compression',
    '1.2.840.10008.1.2.4.81': 'JPEG-LS Lossy (Near-Lossless) Image Compression',
    '1.2.840.10008.1.2.4.90': 'JPEG 2000 Image Compression (Lossless Only)',
    '1.2.840.10008.1.2.4.91': 'JPEG 2000 Image Compression',
    '1.2.840.10008.1.2.4.92': 'JPEG 2000 Part 2 Multi-component Image Compression (Lossless Only)',
    '1.2.840.10008.1.2.4.93': 'JPEG 2000 Part 2 Multi-component Image Compression',
    '1.2.840.10008.1.2.4.94': 'JPIP Referenced',
    '1.2.840.10008.1.2.4.95': 'JPIP Referenced Deflate',
    '1.2.840.10008.1.2.4.100': 'MPEG2 Main Profile / Main Level',
    '1.2.840.10008.1.2.4.101': 'MPEG2 Main Profile / High Level',
    '1.2.840.10008.1.2.4.102': 'MPEG-4 AVC/H.264 High Profile / Level 4.1',
    '1.2.840.10008.1.2.4.103': 'MPEG-4 HEVC/H.265 Main Profile / Level 5.1',
    '1.2.840.10008.1.2.5': 'RLE Lossless'
  };
  
  return syntaxDescriptions[transferSyntaxUID] || `Unknown (${transferSyntaxUID})`;
}